import { useCallback } from 'react';
import AudioIcon from '@public/svgs/file/audio-doc.svg';
import ExcelIcon from '@public/svgs/file/excel-doc.svg';
import KeynoteIcon from '@public/svgs/file/keynote-doc.svg';
import PDFIcon from '@public/svgs/file/pdf-doc.svg';
import PowerPointIcon from '@public/svgs/file/powerpoint-doc.svg';
import ImageIcon from '@public/svgs/file/image-doc.svg';
import URLIcon from '@public/svgs/file/url-doc.svg';
import URLTealIcon from '@public/svgs/file/url-doc-teal.svg';
import VideoIcon from '@public/svgs/file/video-doc.svg';
import WordIcon from '@public/svgs/file/word-doc.svg';
import SkipperPaperIcon from '@public/svgs/file/skipper-paper.svg';
import { GenericDocSVG } from '@components/common/svgs/GenericDocSVG';
import NextImage from 'next/image';

const DOCUMENT_FILE_TYPES = [
    '.pdf',
    '.doc',
    '.docx',
    '.odt',
    '.txt',
    '.xls',
    '.csv',
    '.xlsx',
    '.ods',
    '.ppt',
    '.pptx',
    '.odp',
] as const;

const ANIMATED_IMAGE_FILE_TYPES = ['.gif'];

const IMAGE_FILE_TYPES = ['.png', '.jpg', '.jpeg', '.bmp', '.webp', '.svg', '.heic'] as const;

const ADVANCED_IMAGE_FILE_TYPES = ['.tif', '.tiff', '.raw'] as const;

const AUDIO_FILE_TYPES = [
    '.mp3',
    '.wav',
    '.m4a',
    '.mka',
    '.voc',
    '.mid',
    '.flac',
    '.au',
    '.amr',
    '.aac',
] as const;

const VIDEO_FILE_TYPES = [
    '.mp4',
    '.avi',
    '.3gp',
    '.m4v',
    '.mpeg',
    '.mov',
    '.mkv',
    '.wmv',
    '.webm',
    '.mpg',
    '.flv',
] as const;

const APPLICATION_FILE_TYPES = [
    'vnd.ms-excel',
    'vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'vnd.openxmlformats-officedocument.wordprocessingml.document',
    'vnd.oasis.opendocument.text',
    'vnd.oasis.opendocument.spreadsheet',
    'vnd.oasis.opendocument.presentation',
    'vnd.ms-powerpoint',
    'vnd.openxmlformats-officedocument.presentationml.presentation',
    'pdf',
    'msword',
];
const TEXT_FILE_TYPES = ['plain', 'csv'];

type DocumentTypes = 'docs' | 'images' | 'advanced-images' | 'gifs' | 'audio' | 'video';

export function getAllowedFileExtensions(type: DocumentTypes) {
    if (type === 'docs') return DOCUMENT_FILE_TYPES;
    if (type === 'images') return IMAGE_FILE_TYPES;
    if (type === 'advanced-images') return ADVANCED_IMAGE_FILE_TYPES;
    if (type === 'gifs') return ANIMATED_IMAGE_FILE_TYPES;
    if (type === 'video') return VIDEO_FILE_TYPES;
    if (type === 'audio') return AUDIO_FILE_TYPES;

    return null as never;
}

export function getAllowedMimesByType(...types: DocumentTypes[]) {
    return types.reduce((total, t) => {
        if (t === 'docs') {
            total = total.concat(
                APPLICATION_FILE_TYPES.concat(DOCUMENT_FILE_TYPES).map(
                    (app) => `application/${app?.replace('.', '')}`,
                ),
                TEXT_FILE_TYPES.concat(DOCUMENT_FILE_TYPES).map(
                    (app) => `text/${app?.replace('.', '')}`,
                ),
            );
            return total;
        }
        if (t === 'audio') {
            total = total.concat(AUDIO_FILE_TYPES.map((app) => `audio/${app?.replace('.', '')}`));
            return total;
        }

        if (t === 'images') {
            total = total.concat(IMAGE_FILE_TYPES.map((app) => `image/${app?.replace('.', '')}`));
        }

        if (t === 'advanced-images') {
            total = total.concat(
                ADVANCED_IMAGE_FILE_TYPES.map((app) => `image/${app?.replace('.', '')}`),
            );
        }

        if (t === 'gifs') {
            total = total.concat(
                ANIMATED_IMAGE_FILE_TYPES.map((app) => `image/${app?.replace('.', '')}`),
            );
        }

        if (t === 'video') {
            total = total.concat(VIDEO_FILE_TYPES.map((app) => `video/${app?.replace('.', '')}`));
            return total;
        }

        return total;
    }, [] as any[]);
}

/**
 * This is useful for current uploads and showing correct Icons
 *
 * @param type
 *
 * @returns
 */
export function valueToType(type?: string) {
    if (!type) return 'unknown';
    if (type?.endsWith('ppt')) return 'ppt';
    if (type?.endsWith('pdf')) return 'pdf';
    if (type?.endsWith('png')) return 'image';
    if (type?.endsWith('jpeg')) return 'image';
    if (type?.endsWith('jpg')) return 'image';
    if (type?.endsWith('svg')) return 'image';
    if (type?.endsWith('gif')) return 'image';
    if (type?.endsWith('mp4')) return 'video';
    if (type?.endsWith('mov')) return 'video';
    if (type?.endsWith('mp3')) return 'audio';
    if (type?.endsWith('xls')) return 'excel';
    if (type?.endsWith('doc')) return 'doc';
    return 'unknown';
}

export type AllowedFileTypes =
    | 'images'
    | 'images+gifs'
    | 'advanced-images'
    | 'advanced-images+gifs'
    | 'video'
    | 'audio'
    | 'video/audio'
    | 'avatar'
    | 'limited'
    | 'any';

export function filterByFileTypes(variant: AllowedFileTypes) {
    let mimeTypes: string[] = [];
    let fileExtensions: string[] = [];

    switch (variant) {
        case 'images':
            mimeTypes = getAllowedMimesByType('images');
            fileExtensions = [...getAllowedFileExtensions('images')];
            break;
        case 'images+gifs':
            mimeTypes = getAllowedMimesByType('images', 'gifs');
            fileExtensions = [
                ...getAllowedFileExtensions('images'),
                ...getAllowedFileExtensions('gifs'),
            ];
            break;
        case 'advanced-images':
            mimeTypes = getAllowedMimesByType('images', 'advanced-images');
            fileExtensions = [
                ...getAllowedFileExtensions('images'),
                ...getAllowedFileExtensions('advanced-images'),
            ];
            break;
        case 'advanced-images+gifs':
            mimeTypes = getAllowedMimesByType('images', 'advanced-images', 'gifs');
            fileExtensions = [
                ...getAllowedFileExtensions('images'),
                ...getAllowedFileExtensions('advanced-images'),
                ...getAllowedFileExtensions('gifs'),
            ];
            break;
        case 'video':
            mimeTypes = getAllowedMimesByType('video');
            fileExtensions = [...getAllowedFileExtensions('video')];
            break;
        case 'video/audio':
            mimeTypes = getAllowedMimesByType('video', 'audio');
            fileExtensions = [
                ...getAllowedFileExtensions('video'),
                ...getAllowedFileExtensions('audio'),
            ];
            break;
        case 'any':
            mimeTypes = ['*/*'];
            break;
        default:
            mimeTypes = getAllowedMimesByType('docs', 'images', 'video', 'audio');
            fileExtensions = [
                ...getAllowedFileExtensions('docs'),
                ...getAllowedFileExtensions('images'),
                ...getAllowedFileExtensions('video'),
                ...getAllowedFileExtensions('audio'),
            ];
            break;
    }

    return (file: File) =>
        variant === 'any'
            ? true
            : mimeTypes.some((type) => file?.type?.endsWith(type)) ||
              fileExtensions.some((type) => file?.name?.endsWith(type));
}

/**
 * Need to confirm valid file types as video more than likely should be limited to:
 * mp4,flv,hlvs and?
 *
 * @param variant
 * @returns
 */
export function getAllowedMimeTypes(variant: AllowedFileTypes) {
    return (provider?: 'dropbox') => {
        if (provider === 'dropbox') {
            switch (variant) {
                case 'images':
                    return getAllowedFileExtensions('images');
                case 'images+gifs':
                    return [
                        ...getAllowedFileExtensions('images'),
                        ...getAllowedFileExtensions('gifs'),
                    ];
                case 'advanced-images':
                    return [
                        ...getAllowedFileExtensions('images'),
                        ...getAllowedFileExtensions('advanced-images'),
                    ];
                case 'advanced-images+gifs':
                    return [
                        ...getAllowedFileExtensions('images'),
                        ...getAllowedFileExtensions('advanced-images'),
                        ...getAllowedFileExtensions('gifs'),
                    ];
                case 'video':
                    return getAllowedFileExtensions('video');
                case 'video/audio':
                    return [
                        ...getAllowedFileExtensions('video'),
                        ...getAllowedFileExtensions('audio'),
                    ];
                case 'avatar':
                    return [...getAllowedFileExtensions('images')].filter((f) =>
                        ['.png', '.jpeg', '.jpg'].includes(f),
                    );
                case 'any':
                    return ['*/*'];
                default:
                    return [
                        ...getAllowedFileExtensions('video'),
                        ...getAllowedFileExtensions('images'),
                        ...getAllowedFileExtensions('advanced-images'),
                        ...getAllowedFileExtensions('gifs'),
                        ...getAllowedFileExtensions('audio'),
                        ...getAllowedFileExtensions('docs'),
                    ];
            }
        }

        switch (variant) {
            case 'images':
                return getAllowedMimesByType('images').join(',');
            case 'images+gifs':
                return getAllowedMimesByType('images', 'gifs').join(',');
            case 'advanced-images':
                return getAllowedMimesByType('images', 'advanced-images').join(',');
            case 'advanced-images+gifs':
                return getAllowedMimesByType('images', 'advanced-images', 'gifs').join(',');
            case 'video':
                return getAllowedMimesByType('video').join(',');
            case 'video/audio':
                return getAllowedMimesByType('video', 'audio').join(',');
            case 'avatar':
                return getAllowedMimesByType('images')
                    .filter((f) => ['.png', '.jpeg', '.jpg'].includes(f))
                    .join(',');
            case 'any':
                return '*/*';
            default:
                return [
                    ...getAllowedMimesByType('video'),
                    ...getAllowedMimesByType('images'),
                    ...getAllowedMimesByType('advanced-images'),
                    ...getAllowedMimesByType('gifs'),
                    ...getAllowedMimesByType('audio'),
                    ...getAllowedMimesByType('docs'),
                ].join(',');
        }
    };
}

export function useFileTypeImage() {
    const selectIcon = useCallback(
        ({
            type,
            variant,
            originalFileName,
            small,
        }: {
            type: string;
            variant?: boolean;
            originalFileName?: string;
            small?: boolean;
        }) => {
            const icon = getFileType(type, variant) as any;
            const properties: any = small ? { width: 24, height: 24, layout: 'fixed' } : {};

            if (!!icon) {
                return <NextImage src={icon} {...properties} />;
            }

            const extension = originalFileName
                ?.match(/(\.(\w{1,4}))$/gm)?.[0]
                ?.replace('.', '')
                ?.trim();

            return (
                <span
                    className={`${
                        small ? 'relative w-[24px] h-[24px]' : 'relative w-[40px] h-[40px]'
                    }`}
                >
                    <GenericDocSVG text={extension || ''} />
                </span>
            );
        },
        [],
    );

    return selectIcon;
}

export function getFileType(type: string, variant?: boolean) {
    if (!type) return null;

    switch (true) {
        case !!type?.match(/((image).*|(gif|png|jpeg|jpg|svg|tiff))$/gi)?.length:
            return ImageIcon;
        case !!type?.match(/(audio).*/gi)?.length:
            return AudioIcon;
        case !!type?.match(/(video).*/gi)?.length:
            return VideoIcon;
        case !!type?.match(/(ppt|presentation)$/gi)?.length:
            return PowerPointIcon;
        case !!type?.match(/(excel|spreadsheet).*/gi)?.length:
            return ExcelIcon;
        case !!type?.match(/(doc|msword).*/gi)?.length:
            return WordIcon;
        case !!type?.match(/(skipper_paper).*/gi)?.length:
            return SkipperPaperIcon;
        case !!type?.match(/(pdf).*/gi)?.length:
            return PDFIcon;
        case !!type?.match(/(url).*/gi)?.length:
            if (variant) return URLTealIcon;
            return URLIcon;
        case !!type?.match(/((keynote).*|(key))$/gi)?.length:
            return KeynoteIcon;
        default:
            return null;
    }
}

/**
 * File Materials Color Selection Helper
 *
 * @param type
 * @param variant
 * @returns
 */
export function getBackgroundColorForFile(type: string, variant?: boolean) {
    switch (true) {
        case !!type?.match(/((image).*|(gif|png|jpeg|jpg|svg))$/gi)?.length:
            return 'bg-white';
        case !!type?.match(/(audio|mp3).*/gi)?.length:
            return 'bg-violet-50';
        case !!type?.match(/(video|mov|mp4).*/gi)?.length:
            return 'bg-red-50';
        case !!type?.match(/(ppt|presentation).*/gi)?.length:
            return 'bg-sunshine-050';
        case !!type?.match(/(excel|spreadsheet).*/gi)?.length:
            return 'bg-lime-100';
        case !!type?.match(/(doc|msword).*/gi)?.length:
            return 'bg-ocean-050';
        case !!type?.match(/(pdf).*/gi)?.length:
            return 'bg-red-50';
        case !!type?.match(/(url).*/gi)?.length:
            if (variant) return 'bg-teal-50';
            return 'bg-gray-50';
        case !!type?.match(/(skipper_paper).*/gi)?.length:
            return 'bg-teal-50';
        case !!type?.match(/((keynote).*|(key))$/gi)?.length:
            return 'bg-mango-50';
        default:
            return 'bg-gray-50';
    }
}

export function convertToBytes(num?: number) {
    if (!num) return '';
    let divisor = num / 1024;
    let suffix = divisor > 1024 ? 'MB' : 'KB';
    if (divisor > 1024) {
        divisor = divisor / 1024;
    }
    return `${divisor.toFixed(1)} ${suffix}`;
}

export const PDFTRON_VIEWABLE_FILE_TYPES = ['image', 'pdf', 'excel', 'ppt', 'doc', 'text'];
